import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { DEFAULT_QUANTIFY_SORT_BY } from '@modules/quantify/constants';
import {
  useFetchQuantifyReport,
  useGetQueryContainerForAllFiltersApplied,
  useTriggerQuantifyQueryAnalyticsEvent,
} from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { QuantifyState } from '@modules/quantify/store/types';
import {
  getVisibleDataPointsDefaultSize,
  getVisualisationTypeIcon,
  getVisualisationTypesForGroupByField,
  isAggregateFormula,
  isValidQuery,
} from '@modules/quantify/utils';
import { Box, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { QuantifyVisualisationType } from 'generated/graphql';
import { useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { mapFromEdgeScope } from 'v2/core/repository/edge-server/analysis/fromEdgeAnalysis';
import { EQLFunctionName } from 'v2/core/repository/eql/types';
import { shallow } from 'zustand/shallow';

const trackQuantifyReportVisualisationEvents = createAnalyticsTracker(
  'quantifyReportVisualisation',
);

export function QuantifyVisualisationTypeToggleButton() {
  const { showNotification } = useNotificationContext();

  const [shouldExecuteQuery, setShouldExecuteQuery] = useState(false);
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);

  const {
    activeVisualisationType,
    topDataPointsSelectionCount,
    query,
    isChartQueryInProgress,
    isTableQueryInProgress,
    shouldSkipTrendChart,
  } = useQuantifyStore(({ query, visual }) => {
    return {
      query,
      activeVisualisationType: query.visualisationType,
      topDataPointsSelectionCount: query.topDataPointsSelectionCount,
      isTableQueryInProgress: visual.isTableQueryInProgress,
      shouldSkipTrendChart: visual.shouldSkipTrendChart,
      isChartQueryInProgress: visual.isChartQueryInProgress,
    };
  }, shallow);

  const { WhereClause } = useGetQueryContainerForAllFiltersApplied();

  const fetchReportData = useFetchQuantifyReport();

  const quantifyQueryAnalyticsEvent = useTriggerQuantifyQueryAnalyticsEvent();

  useUpdateEffect(() => {
    if (!shouldExecuteQuery) {
      return;
    }

    setShouldExecuteQuery(false);
    fetchReportData();
    quantifyQueryAnalyticsEvent('Quantify - Query Executed');
  }, [shouldExecuteQuery]);

  const { name, href, scope } = useAnalysisStore(
    (analysis) => ({
      name: analysis.title,
      href: analysis.href,
      scope: analysis.scope,
    }),
    shallow,
  );

  const onVisualisationTypeChange = (
    _: React.MouseEvent<HTMLElement, MouseEvent>,
    value: QuantifyVisualisationType | null,
  ) => {
    if (!value) {
      return;
    }

    const updatedTopDataPointsSelectionCount = getVisibleDataPointsDefaultSize(
      value,
      topDataPointsSelectionCount,
    );

    const queryPayload: Partial<QuantifyState['query']> = {
      topDataPointsSelectionCount: updatedTopDataPointsSelectionCount,
      visualisationType: value,
    };

    const isIncompatibleVisualisationChange =
      value === QuantifyVisualisationType.Trend ||
      activeVisualisationType === QuantifyVisualisationType.Trend;

    if (isIncompatibleVisualisationChange) {
      queryPayload.sortBy = DEFAULT_QUANTIFY_SORT_BY;
    }

    const shouldSwitchToNormaliseByPercent =
      value === QuantifyVisualisationType.Pie &&
      query.formulaQuery?.eqlFunction === EQLFunctionName.Count;

    if (shouldSwitchToNormaliseByPercent) {
      queryPayload.formulaQuery = {
        ...query.formulaQuery,
        denominatorWhereClause: WhereClause,
        eqlFunction: EQLFunctionName.NormaliseByPercent,
      };

      showNotification({
        contentComponent: 'Calculation switched to Percentage for Pie.',
        props: {
          severity: 'info',
          isClosable: true,
          timeout: 10 * 1000, // 10 seconds
        },
      });
    }

    dispatch({
      type: QuantifyActionType.SET_QUERY,
      payload: queryPayload,
    });

    dispatch({
      type: QuantifyActionType.SET_REPORT_DATA,
      payload: {
        quantify: {
          tableRows: null,
          chartDataPoints: null,
          aggregateFormulaResponse: null,
        },
        compare: {
          tableRows: null,
          chartDataPoints: null,
          aggregateFormulaResponse: null,
        },
      },
    });

    setShouldExecuteQuery(true);

    trackQuantifyReportVisualisationEvents({
      eventName: 'Quantify Report - Change Visualisation',
      newVisualisationType: value,
      visualisationType: activeVisualisationType,
      name,
      link: href,
      scope: mapFromEdgeScope(scope),
    });
  };

  const isInvalidQuery = !isValidQuery(query);

  const visualisationTypesToDisplay = useMemo(() => {
    const skipPieChart = isAggregateFormula(query.formulaQuery?.eqlFunction);

    const visualisationTypes = getVisualisationTypesForGroupByField({
      groupBy: query.groupByField,
      skipPieChart,
      skipTrendChart: shouldSkipTrendChart,
      measuredAsCriteria: query.formulaQuery?.criteria,
    });

    return visualisationTypes;
  }, [
    query.groupByField,
    query.formulaQuery?.eqlFunction,
    query.formulaQuery?.criteria,
    shouldSkipTrendChart,
  ]);

  const isValidMeasureAsCriteria = !!query.formulaQuery?.criteria;
  const isButtonGroupDisabled =
    isTableQueryInProgress ||
    isChartQueryInProgress ||
    !isValidQuery(query) ||
    !isValidMeasureAsCriteria;

  return (
    <ToggleButtonGroup
      color="primary"
      value={isInvalidQuery ? undefined : activeVisualisationType}
      exclusive
      onChange={onVisualisationTypeChange}
      aria-label="Visualisation Type"
      data-disabled={isButtonGroupDisabled ? 'true' : 'false'}
      disabled={isButtonGroupDisabled}
      size="small"
    >
      {visualisationTypesToDisplay.map((item) => {
        return (
          <ToggleButton
            key={item}
            value={item}
            data-role={`${item}-visualisation-type-toggle`}
            sx={{
              whiteSpace: 'nowrap',
              minWidth: 'max-content',
            }}
          >
            <Stack spacing={0.5} direction="row" alignItems="center">
              {getVisualisationTypeIcon(item)}
              <Box>
                {item === QuantifyVisualisationType.Trend
                  ? 'Trend + Anomalies'
                  : item}
              </Box>
            </Stack>
          </ToggleButton>
        );
      })}
    </ToggleButtonGroup>
  );
}
